import { useEffect, useState } from 'react';
import { benefitsApi } from '../../../api/benefits.js';

const cents = (v) => (v == null ? '—' : `IDR ${Number(v).toLocaleString('en-US')}`);
const TYPES = ['medical', 'dental', 'vision', 'life', 'disability', 'retirement', 'hsa', 'fsa'];

export default function Plans() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);

  function refresh() {
    setLoading(true);
    benefitsApi.listPlans()
      .then((d) => setItems(d?.items || []))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function submit(e) {
    e.preventDefault();
    // Capture the form node before any await: React nulls `e.currentTarget`
    // once the event handler returns, so reading it post-await throws.
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await benefitsApi.createPlan({
        name: fd.get('name'),
        type: fd.get('type'),
        carrier: fd.get('carrier') || null,
        plan_year: Number(fd.get('plan_year')),
        employee_cost_cents: Number(fd.get('employee_cost_cents') || 0),
        employer_cost_cents: Number(fd.get('employer_cost_cents') || 0),
      });
      form.reset();
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create');
    }
  }

  async function toggleActive(plan) {
    try {
      await benefitsApi.updatePlan(plan.id, { active: !plan.active });
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update');
    }
  }

  return (
    <section data-testid="benefits-plans-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Benefit Plan Catalog</h1>
          <p className="muted">{items.length} plans</p>
        </div>
        <button className="btn primary" data-testid="benefits-plan-create" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Close' : '+ New Plan'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="benefits-plans-error">{error}</div>}

      {showForm && (
        <form onSubmit={submit} className="card form-card grid-form">
          <label>Name<input name="name" data-testid="benefits-plan-name-input" required /></label>
          <label>Type
            <select name="type" data-testid="benefits-plan-type-input" defaultValue="medical">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>Carrier<input name="carrier" data-testid="benefits-plan-carrier-input" /></label>
          <label>Plan Year<input name="plan_year" type="number" min="2024" max="2040" defaultValue={new Date().getFullYear()} data-testid="benefits-plan-year-input" required /></label>
          <label>Employee Cost (cents/period)<input name="employee_cost_cents" type="number" min="0" defaultValue="0" data-testid="benefits-plan-emp-cost-input" /></label>
          <label>Employer Cost (cents/period)<input name="employer_cost_cents" type="number" min="0" defaultValue="0" data-testid="benefits-plan-er-cost-input" /></label>
          <div className="full actions">
            <button className="btn primary" type="submit" data-testid="benefits-plan-submit">Create Plan</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="benefits-plan-list">
          <thead>
            <tr><th>Name</th><th>Type</th><th>Carrier</th><th>Year</th><th>Emp Cost/Period</th><th>Active</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`benefits-plan-card-${p.id}`}>
                <td>{p.name}</td>
                <td><span className={`chip type-${p.type}`}>{p.type}</span></td>
                <td>{p.carrier || '—'}</td>
                <td>{p.plan_year}</td>
                <td>{cents(p.employee_cost_cents)}</td>
                <td>
                  <span className={`chip ${p.active ? 'status-active' : 'status-closed'}`}>
                    {p.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <button
                    className="btn ghost small"
                    data-testid={`benefits-plan-toggle-${p.id}`}
                    onClick={() => toggleActive(p)}
                  >
                    {p.active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="muted">No plans yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
